import { Alert, Button, Drawer, Form, Grid, Select, Space } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { emptyPerson, PersonFields, personBody, type PersonFormValues } from "./PersonFields.tsx";
import { ProfileTelegramLink } from "./TelegramLinkState.tsx";

type TeacherDetails = components["schemas"]["TeacherDetails"];

interface TeacherForm extends PersonFormValues {
  subjectIds: string[];
}

interface Props {
  open: boolean;
  /** Teacher being edited, or null to create one. */
  teacher: TeacherDetails | null;
  onClose(): void;
  onSaved(teacher: TeacherDetails, created: boolean): void;
}

function sameSet(a: readonly string[], b: readonly string[]) {
  return a.length === b.length && a.every((id) => b.includes(id));
}

export function TeacherDrawer({ open, teacher, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<TeacherForm>();
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  const subjects = useQuery({
    queryKey: ["academic", "subjects"],
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/subjects");
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: async (values: TeacherForm) => {
      const person = personBody(values);
      const subjectIds = values.subjectIds ?? [];
      if (!teacher) {
        const result = await api.POST("/teachers", { body: { ...person, subjectIds } });
        if (!result.data) throw new ApiError(result.response.status, result.error);
        return result.data;
      }
      const path = { params: { path: { id: teacher.id } } };
      const updated = await api.PUT("/teachers/{id}", { ...path, body: person });
      if (!updated.data) throw new ApiError(updated.response.status, updated.error);
      if (sameSet(subjectIds, teacher.subjects.map((s) => s.id))) return updated.data;
      const withSubjects = await api.PUT("/teachers/{id}/subjects", { ...path, body: { subjectIds } });
      if (!withSubjects.data) throw new ApiError(withSubjects.response.status, withSubjects.error);
      return withSubjects.data;
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ["teachers"] });
      setServerErrors([]);
      onSaved(saved, !teacher);
    },
    onError: (err: unknown) => setServerErrors(applyFieldErrors(form, err, t("app.error"))),
  });

  const initialValues: TeacherForm = teacher
    ? {
        lastName: teacher.lastName,
        firstName: teacher.firstName,
        middleName: teacher.middleName ?? "",
        phone: teacher.phone,
        additionalPhones: teacher.additionalPhones,
        email: teacher.email ?? "",
        subjectIds: teacher.subjects.map((s) => s.id),
      }
    : { ...emptyPerson, subjectIds: [] };

  return (
    <Drawer
      title={teacher ? t("teachers.edit") : t("teachers.create")}
      open={open}
      onClose={onClose}
      size={screens.md ? 560 : "100%"}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="primary" loading={mutation.isPending} onClick={() => form.submit()}>
            {t("common.save")}
          </Button>
        </Space>
      }
    >
      {serverErrors.length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} role="alert" title={serverErrors.map((m) => <div key={m}>{m}</div>)} />
      )}
      <Form<TeacherForm> form={form} layout="vertical" requiredMark={false} initialValues={initialValues} onFinish={(values) => mutation.mutate(values)}>
        <PersonFields />
        <Form.Item name="subjectIds" label={t("teachers.subjects")} extra={teacher && !teacher.isActive ? t("teachers.inactiveSubjectsHint") : t("teachers.subjectsHint")}>
          <Select
            mode="multiple"
            allowClear
            showSearch={{ optionFilterProp: "label" }}
            loading={subjects.isPending}
            status={subjects.isError ? "error" : ""}
            options={(subjects.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
          />
        </Form.Item>
        {teacher && (
          <Form.Item label={t("telegram.label")}>
            <ProfileTelegramLink kind="Teacher" profileId={teacher.id} name={teacher.fullName} canUnlink />
          </Form.Item>
        )}
      </Form>
    </Drawer>
  );
}
